

































































import {
    defineComponent,
    PropType,
    onMounted,
    computed,
    watch,
    reactive,
    ref,
} from '@vue/composition-api';
import Drawer from '@/kt-components/Drawer/index.vue';
import { Button } from '@knowledge/kt-ui';
import Form, { useForm } from '@/components/Form';
import LabelField from './LabelField.vue';
import Label from './Label.vue';
import Input from './Input.vue';
import AddressStrip from './AddressStrip.vue';
import { useApolloModel } from 'vue-apollo-model';
import AddressModel, { Address } from '@/views/Address/AddressModel';
import useForeground from '@/hooks/useForeground';
import { Toast } from '@ks/sharp-ui';
import Price from '@/components/Price/index.vue';
import { VoucherData, BearerType, DiscountType } from '@/views/Detail/model';
import { useLog } from '@/logger';
import useStorageListener from '@/hooks/useStorageListener';
import yoda from '@/yoda';

export default defineComponent({
    name: 'PayCounterDialog',
    components: {
        Drawer,
        Price,
        KTInput: Input,
        KTForm: Form,
        LabelField,
        AddressStrip,
        KTButton: Button,
        Label,
    },
    props: {
        courseId: {
            type: [String, Number],
        },
        model: {
            type: Object,
            required: true,
        },
        lessonId: Number,
        nonage: Boolean, // 未成年
        name: String, // 支付名称
        originPrice: {
            type: Number, // 原价
            required: true,
        },
        price: {
            type: Number, // 实际支付价格
            required: true,
        },
        userBalance: {
            type: Number, // 账户余额
            required: true,
        },
        vouchers: {
            type: Array as PropType<VoucherData[]>,
            default: () => [],
        },
        visible: Boolean,
        isPaying: Boolean, // 控制按钮点击
        useKscoinPay: Boolean,
        useSecKillPrice: Boolean, // 秒杀价
    },
    setup(props, ctx) {
        const log = useLog();
        const addressModel = useApolloModel(AddressModel);
        const form = useForm();
        const data = reactive<{
            remark: string;
            pay: string;
            address?: Address;
        }>({
            remark: '',
            pay: 'wechat',
            address: undefined,
        });
        // 完整的地址显示
        const addressStr = computed(() => {
            if (data.address) {
                return `${data.address.consignee} ${data.address.mobile} ${String(
                    data.address.province,
                ) +
                    String(data.address.city) +
                    data.address.district +
                    data.address.address}`;
            }
            return undefined;
        });
        // 是否需要填写地址
        const showAddress = computed(() => {
            return !!props.model.gifts.length;
        });
        // 收银台title
        const title = computed(() => (props.model.gifts.length ? '领取实物赠品' : '收银台'));
        watch(
            () => addressModel.addressList,
            addresses => {
                // 没有地址，默认地址列表中的第一个
                if (!data.address && addresses.length > 0) {
                    data.address = addresses[0];
                }
            },
            { immediate: true },
        );
        // 选择地址的监听
        useStorageListener(e => {
            data.address = JSON.parse(e.newValue!);
        }, 'selectedAddress');

        useForeground(() => {
            addressModel.getAddressList();
        });

        const toUnitPrice = (price: number, useKscoinPay: boolean) => {
            return useKscoinPay
                ? `${Math.round(price / 10 || 0)}快币`
                : `￥${Math.round((price / 100 || 0) * 10) / 10}`;
        };
        const btnText = computed(() => {
            if (props.useKscoinPay) {
                return props.userBalance >= props.price / 10 ? '立即支付' : '余额不足，充值并支付';
            }
            return '立即支付';
        });
        const canDiscount = computed(() => {
            return props.vouchers.length > 0;
        });

        // 优惠后的价格基数
        const discountDetail = computed(() => {
            const BearerTypeMap = {
                [BearerType.PLATFORM]: '平台券',
                [BearerType.SHOP]: '商家券',
            };
            const discountValueMap = (voucher: VoucherData, restPrice: number) => {
                return {
                    [DiscountType.FREE]: props.originPrice - 10,
                    [DiscountType.PART]: voucher.discountValue,
                    [DiscountType.SALE]: (restPrice * (100 - voucher.discountValue)) / 100,
                };
            };
            let restPrice = props.originPrice;

            return props.vouchers
                .map(voucher => {
                    const discount = discountValueMap(voucher, restPrice)[voucher.discountType];
                    // 在多个券存在的情况下，折扣券可用价格可能需要在优惠后价格基数进行
                    if (voucher.discountType === 3) {
                        restPrice = (restPrice * voucher.discountValue) / 100;
                    } else if (voucher.discountType === 2) {
                        restPrice = restPrice - voucher.discountValue;
                    }

                    return (
                        BearerTypeMap[voucher.bearerType] +
                        toUnitPrice(discount, Boolean(props.useKscoinPay))
                    );
                })
                .join('+');
        });
        // button是否能点击
        const disabled = computed(() => {
            // 执行中不可点，余额不足且为成年不可点
            return props.isPaying || (props.userBalance < props.price / 10 && props.nonage);
        });
        const logInfo = computed(() => {
            const ext: Record<string, any> = {
                isCoinEnough: props.userBalance >= props.price / 10,
            };
            if (canDiscount.value) {
                // TODO: 收银台埋点
                // ext.coupon_id = props.coupon.voucherId ?? '';
                // ext.activity_id = this.coupon.activityId;
                // ext.course_id = this.courseId;
                // ext.lesson_id = this.lessonId || undefined;
            }
            return ext;
        });

        watch(
            () => props.visible,
            vis => {
                if (vis) {
                    showAddress.value && addressModel.getAddressList();
                    log(
                        {
                            action: 'CASHIER_POPUP',
                            params: {
                                is_coupon: props.vouchers.length && 1,
                            },
                        },
                        'show',
                    );
                }
            },
            { immediate: true, deep: true },
        );

        onMounted(() => {
            // Log: 快币收银台
            if (yoda.isInYoda) {
                window.ksLog?.sendCountTag('Lightks_KsCoinPayCounterDialog');
            }
        });

        const handleClose = () => {
            if (props.isPaying) {
                return;
            }
            ctx.root.$log2('Button', 'PayCounter_Close_Btn', 'Impression', logInfo.value);
            ctx.emit('close');
        };
        // 支付
        const handlePay = async () => {
            log(
                {
                    action: 'CASHIER_POPUP',
                    params: {
                        is_coupon: props.vouchers.length && 1,
                        address: data.address?.addressId,
                    },
                },
                'click',
            );
            if (showAddress.value) {
                const validateMessages = await form.validateFields();
                if (validateMessages.length) {
                    Toast.show({
                        content: validateMessages[0].errors[0],
                    });
                } else {
                    ctx.emit('pay', {
                        giftAddressId: data.address?.addressId,
                        remark: data.remark,
                    });
                }
            } else {
                ctx.emit('pay');
            }
        };
        return {
            data,
            form,
            title,
            showAddress,
            disabled,
            addressStr,
            canDiscount,
            btnText,
            discountDetail,
            handlePay,
            handleClose,
        };
    },
});
